import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Form, Input, Button, Checkbox, InputNumber, ColProps, Select, InputNumberProps, CheckboxProps, DatePickerProps, InputProps } from "antd";
import { FormInstance, Rule } from "antd/lib/form";
import { ButtonType } from "antd/lib/button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { DataSelect, DataSelectProps } from "./dataSelect";
import { EnumSelect, EnumSelectProps } from "./enumSelect";
import { FormListFieldData } from "antd/lib/form/FormList";
import { NamePath } from "antd/lib/form/interface";
import { DatePicker as JalaliDatePicker } from "antd-jalali";
import { numUtils } from "../utils/numUtils";
import { RangePickerProps } from "antd/lib/date-picker";
import dayjs, { Dayjs } from "dayjs";
import { ValidateStatus } from "antd/lib/form/FormItem";
import TextArea, { TextAreaProps } from "antd/lib/input/TextArea";
import { IImgSelectProps, ImgSelect } from "./imgSelect";

type DefaultFormProps = {
  children: React.ReactNode;
  layout?: "horizontal" | "vertical" | "inline";
  initialValues?: { [x: string]: any } | null;
  onFinish: (values: any) => void;
  onFinishFailed?: (errorInfo: any) => void;
  form?: FormInstance<any>;
};

const DefaultForm = ({ children, layout = "vertical", initialValues = null, onFinish, onFinishFailed, form }: DefaultFormProps) => {
  const formItemLayout = useMemo(
    () =>
      layout === "horizontal"
        ? {
            labelCol: { span: 6 },
            wrapperCol: { span: 18 },
          }
        : null,
    [layout]
  );

  return (
    <Form form={form} layout={layout} {...formItemLayout} initialValues={{ ...initialValues }} onFinish={onFinish} onFinishFailed={onFinishFailed}>
      {children}
    </Form>
  );
};

interface DefaultFormTextInputProps {
  name: NamePath;
  label?: string | "none";
  rules?: Rule[];
  className?: string;
  inputClassName?: string;
  labelCol?: ColProps;
  wrapperCol?: ColProps;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onChangeCallback?: React.ChangeEventHandler<HTMLInputElement>;
  validateStatus?: ValidateStatus;
  disabled?: boolean;
}

const TextInput = ({
  name,
  label,
  rules,
  className,
  inputClassName,
  labelCol,
  wrapperCol,
  onChange,
  onChangeCallback,
  validateStatus,
  disabled,
  prefix,
  suffix,
  addonBefore,
  addonAfter,
}: DefaultFormTextInputProps & Omit<InputProps, "name">) => {
  const [t] = useTranslation();

  const itemProps = useMemo(() => {
    let res: { [x: string]: any } = { name };

    if (!label) {
      res.label = t(name instanceof Array ? name[1].toString() : name.toString());
    } else if (label !== "none") {
      res.label = label;
    }

    if (rules) {
      res.rules = rules;
    }

    if (className) {
      res.className = className;
    }

    return res;
  }, [className, label, name, rules, t]);

  const handleChange = useCallback(
    (e: any) => {
      if (onChange) {
        onChange(e);
      }
      if (onChangeCallback) {
        onChangeCallback(e);
      }
    },
    [onChange, onChangeCallback]
  );

  return (
    <Form.Item {...itemProps} labelCol={labelCol} wrapperCol={wrapperCol} validateStatus={validateStatus}>
      <Input
        onChange={handleChange}
        disabled={disabled}
        prefix={prefix}
        suffix={suffix}
        className={inputClassName}
        addonBefore={addonBefore}
        addonAfter={addonAfter}
      />
    </Form.Item>
  );
};

DefaultForm.TextInput = TextInput;

const TextAreaInput = ({
  name,
  label,
  rules,
  className,
  labelCol,
  wrapperCol,
  onChange,
  onChangeCallback,
  validateStatus,
  disabled,
}: DefaultFormTextInputProps & Omit<TextAreaProps, "name">) => {
  const [t] = useTranslation();
  let itemProps: { [x: string]: any } = { name };

  if (!label) {
    itemProps.label = t(name instanceof Array ? name[1].toString() : name.toString());
  } else if (label !== "none") {
    itemProps.label = label;
  }

  if (rules) {
    itemProps.rules = rules;
  }

  if (className) {
    itemProps.className = className;
  }

  const handleChange = (e: any) => {
    if (onChange) {
      onChange(e);
    }
    if (onChangeCallback) {
      onChangeCallback(e);
    }
  };

  return (
    <Form.Item {...itemProps} labelCol={labelCol} wrapperCol={wrapperCol} validateStatus={validateStatus}>
      <TextArea onChange={handleChange} disabled={disabled} />
    </Form.Item>
  );
};

DefaultForm.TextAreaInput = TextAreaInput;

interface DefaultFormNumberInputProps extends Omit<InputNumberProps<number | string>, "name"> {
  name: NamePath;
  label?: string | "none";
  rules?: Rule[];
  className?: string;
  inputClassName?: string;
  min?: number;
  max?: number;
  labelCol?: ColProps;
  wrapperCol?: ColProps;
  onChange?: (value: number | string | null) => void;
  onChangeCallback?: (value: number | string) => void;
  validateStatus?: ValidateStatus;
}

const NumberInput = ({
  name,
  label,
  rules,
  className,
  inputClassName,
  wrapperCol,
  labelCol,
  validateStatus,
  onChange,
  onChangeCallback,
  ...rest
}: DefaultFormNumberInputProps) => {
  const [t] = useTranslation();
  let itemProps: { [x: string]: any } = { name };

  if (!label) {
    itemProps.label = t(name instanceof Array ? name[1].toString() : name.toString());
  } else if (label !== "none") {
    itemProps.label = label;
  }

  if (rules) {
    itemProps.rules = rules;
  }

  if (className) {
    itemProps.className = className;
  }

  const handleChange = (value: any) => {
    if (onChange) {
      onChange(value);
    }
    if (onChangeCallback) {
      onChangeCallback(value);
    }
  };

  return (
    <Form.Item {...itemProps} wrapperCol={wrapperCol} validateStatus={validateStatus} labelCol={labelCol}>
      <InputNumber className={`w-full ${inputClassName ?? ""}`} onChange={handleChange} {...rest} />
    </Form.Item>
  );
};

DefaultForm.NumberInput = NumberInput;

const PriceInput = ({ step, ...rest }: Omit<DefaultFormNumberInputProps, "formatter" | "parser">) => {
  const formatter = (value?: string | number) => (value ? numUtils.commaSperate(value) : "0");
  const parser = (value?: string) => (value ? value.replace(/\$\s?|(,*)/g, "") : 0);

  return <NumberInput formatter={formatter} parser={parser} step={step || 1000} {...rest} />;
};

DefaultForm.PriceInput = PriceInput;

const PercentInput = ({ min, max, ...rest }: Omit<DefaultFormNumberInputProps, "formatter" | "parser">) => {
  const formatter = (value?: string | number) => `${value}%`;
  const parser = (value?: string) => (value ? value.replace("%", "") : 0);

  return <NumberInput formatter={formatter} parser={parser} min={min || 0} max={max || 100} {...rest} />;
};

DefaultForm.PercentInput = PercentInput;

type DefaultFormPasswordInputProps = {
  name: NamePath;
  label?: string | "none";
  rules?: Rule[];
  className?: string;
  labelCol?: ColProps;
  wrapperCol?: ColProps;
};

const PasswordInput = ({ name, label, rules, className, labelCol, wrapperCol }: DefaultFormPasswordInputProps) => {
  const [t] = useTranslation();
  let itemProps: { [x: string]: any } = { name };

  if (!label) {
    itemProps.label = t(name instanceof Array ? name[1].toString() : name.toString());
  } else if (label !== "none") {
    itemProps.label = label;
  }

  if (rules) {
    itemProps.rules = rules;
  }

  if (className) {
    itemProps.className = className;
  }

  return (
    <Form.Item {...itemProps} labelCol={labelCol} wrapperCol={wrapperCol}>
      <Input.Password />
    </Form.Item>
  );
};

DefaultForm.PasswordInput = PasswordInput;

type DefaultFormHiddenInputProps = {
  name: NamePath;
};

const HiddenInput = ({ name }: DefaultFormHiddenInputProps) => {
  return (
    <Form.Item name={name} hidden={true}>
      <Input type="hidden" />
    </Form.Item>
  );
};

DefaultForm.HiddenInput = HiddenInput;

interface DefaultFormCheckboxProps extends Omit<CheckboxProps, "name"> {
  name: NamePath;
  label: string;
  required?: boolean;
  requiredMessage?: string;
  wrap?: boolean;
  className?: string;
}

const DefaultCheckbox = ({ name, label, required, requiredMessage, wrap, className, ...rest }: DefaultFormCheckboxProps) => {
  let itemProps: { [x: string]: any } = { name };

  if (required) {
    itemProps.rules = [
      {
        validator: (_: any, value: any) => (value ? Promise.resolve() : Promise.reject(new Error(requiredMessage || "Should accept agreement"))),
      },
    ];
  }

  if (wrap) {
    itemProps.wrapperCol = { span: 18, offset: 6 };
  }

  if (className) {
    itemProps.className = className;
  }

  return (
    <Form.Item valuePropName="checked" {...itemProps}>
      <Checkbox {...rest}>{label !== "none" ? label : null}</Checkbox>
    </Form.Item>
  );
};

DefaultForm.Checkbox = DefaultCheckbox;

interface DefaultFormDataSelectProps extends DataSelectProps {
  name: NamePath;
  label?: string | "none";
  rules?: Rule[];
  className?: string;
  labelCol?: ColProps;
  wrapperCol?: ColProps;
}

const DefaultDataSelect = ({ name, label, rules, wrapperCol, className, labelCol, ...rest }: DefaultFormDataSelectProps) => {
  const [t] = useTranslation();
  let itemProps: { [x: string]: any } = { name };

  if (!label) {
    itemProps.label = t(name instanceof Array ? name[1].toString() : name.toString());
  } else if (label !== "none") {
    itemProps.label = label;
  }

  if (rules) {
    itemProps.rules = rules;
  }

  if (className) {
    itemProps.className = className;
  }

  return (
    <Form.Item {...itemProps} labelCol={labelCol} wrapperCol={wrapperCol}>
      <DataSelect {...rest} />
    </Form.Item>
  );
};

DefaultForm.DataSelect = DefaultDataSelect;

interface DefaultFormEnumSelectProps extends EnumSelectProps {
  name: NamePath;
  label?: string | "none";
  rules?: Rule[];
  className?: string;
  labelCol?: ColProps;
  wrapperCol?: ColProps;
}

const DefaultEnumSelect = ({ name, label, rules, className, labelCol, wrapperCol, ...rest }: DefaultFormEnumSelectProps) => {
  const [t] = useTranslation();
  let itemProps: { [x: string]: any } = { name };

  if (!label) {
    itemProps.label = t(name instanceof Array ? name[1].toString() : name.toString());
  } else if (label !== "none") {
    itemProps.label = label;
  }

  if (rules) {
    itemProps.rules = rules;
  }

  if (className) {
    itemProps.className = className;
  }

  return (
    <Form.Item {...itemProps} labelCol={labelCol} wrapperCol={wrapperCol}>
      <EnumSelect {...rest} />
    </Form.Item>
  );
};

DefaultForm.EnumSelect = DefaultEnumSelect;

interface DefaultFormImgSelectProps extends IImgSelectProps {
  name: NamePath;
  label?: string | "none";
  rules?: Rule[];
  className?: string;
  labelCol?: ColProps;
  wrapperCol?: ColProps;
}

const DefaultImgSelect = ({ name, label, rules, wrapperCol, className, labelCol, ...rest }: DefaultFormImgSelectProps) => {
  const [t] = useTranslation();
  let itemProps: { [x: string]: any } = { name };

  if (!label) {
    itemProps.label = t(name instanceof Array ? name[1].toString() : name.toString());
  } else if (label !== "none") {
    itemProps.label = label;
  }

  if (rules) {
    itemProps.rules = rules;
  }

  if (className) {
    itemProps.className = className;
  }

  return (
    <Form.Item {...itemProps} labelCol={labelCol} wrapperCol={wrapperCol}>
      <ImgSelect {...rest} />
    </Form.Item>
  );
};

DefaultForm.DefaultImgSelect = DefaultImgSelect;

type DefaultFormPlainTextProps = {
  label: NamePath;
  text?: string;
  className?: string;
};

const PlainText = ({ label, text, className }: DefaultFormPlainTextProps) => {
  let itemProps: { [x: string]: any } = { label };

  if (className) {
    itemProps.className = className;
  }

  return (
    <Form.Item {...itemProps}>
      <span>{text}</span>
    </Form.Item>
  );
};

DefaultForm.PlainText = PlainText;

type DefaultFormTagSelectProps = {
  name: NamePath;
  label?: string | "none";
  rules?: Rule[];
  className?: string;
  labelCol?: ColProps;
  wrapperCol?: ColProps;
  placeholder?: string;
};

const TagSelect = ({ name, label, rules, className, labelCol, wrapperCol, placeholder }: DefaultFormTagSelectProps) => {
  const [t] = useTranslation();
  let itemProps: { [x: string]: any } = { name };

  if (!label) {
    itemProps.label = t(name instanceof Array ? name[1].toString() : name.toString());
  } else if (label !== "none") {
    itemProps.label = label;
  }

  if (rules) {
    itemProps.rules = rules;
  }

  if (className) {
    itemProps.className = className;
  }

  return (
    <Form.Item {...itemProps} labelCol={labelCol} wrapperCol={wrapperCol}>
      <Select mode="tags" placeholder={placeholder || "انتخاب کنید"} notFoundContent={null} />
    </Form.Item>
  );
};

DefaultForm.TagSelect = TagSelect;

type DefaultFormListProps = {
  name: string | number | (string | number)[];
  rules?: Rule[];
  children: (fields: FormListFieldData[], add: (defaultValue?: any) => void, remove: (index: number) => void) => React.ReactNode;
};

const DefaultList = ({ name, rules, children }: DefaultFormListProps) => {
  let listProps: { [x: string]: any } = {};

  if (rules) {
    listProps.rules = rules;
  }

  return (
    <Form.List name={name} {...listProps}>
      {(fields, { add, remove }, { errors }) => <>{children(fields, add, remove)}</>}
    </Form.List>
  );
};

DefaultForm.List = DefaultList;

type DefaultFormSubmitButtonProps = {
  children?: React.ReactNode;
  buttonType?: ButtonType;
  wrap?: boolean;
  icon?: React.ReactNode;
  className?: string;
  loading?: boolean;
};

const SubmitButton = ({ children, buttonType = "primary", wrap, icon, className, loading }: DefaultFormSubmitButtonProps) => {
  let itemProps: { [x: string]: any } = { type: buttonType };

  if (wrap) {
    itemProps.wrapperCol = { span: 18, offset: 6 };
  }

  if (className) {
    itemProps.className = className;
  }

  return (
    <Form.Item {...itemProps}>
      <Button type={buttonType} htmlType="submit" loading={loading || false} icon={icon || <FontAwesomeIcon className="anticon" icon={faCheck} />}>
        {children || "ثبت"}
      </Button>
    </Form.Item>
  );
};

DefaultForm.SubmitButton = SubmitButton;

const DatePicker = ({
  name,
  label,
  rules,
  className,
  labelCol,
  wrapperCol,
  value,
  ...rest
}: DefaultFormTextInputProps & Omit<DatePickerProps, "name">) => {
  const [t] = useTranslation();
  let itemProps: { [x: string]: any } = { name };

  if (!label) {
    itemProps.label = t(name instanceof Array ? name[1].toString() : name.toString());
  } else if (label !== "none") {
    itemProps.label = label;
  }

  if (rules) {
    itemProps.rules = rules;
  }

  if (className) {
    itemProps.className = className;
  }

  return (
    <Form.Item {...itemProps} labelCol={labelCol} wrapperCol={wrapperCol}>
      <JalaliDatePicker {...rest} />
    </Form.Item>
  );
};

DefaultForm.DatePicker = DatePicker;

const RangePicker = ({
  name,
  label,
  rules,
  className,
  labelCol,
  wrapperCol,
  defaultValue,
  ...rest
}: DefaultFormTextInputProps & { defaultValue?: [Date?, Date?] | undefined } & Omit<RangePickerProps, "name" | "defaultValue">) => {
  const [t] = useTranslation();
  let itemProps: { [x: string]: any } = { name };

  if (!label) {
    itemProps.label = t(name instanceof Array ? name[1].toString() : name.toString());
  } else if (label !== "none") {
    itemProps.label = label;
  }

  if (rules) {
    itemProps.rules = rules;
  }

  if (className) {
    itemProps.className = className;
  }
  let _defaultVlue: [Dayjs | null, Dayjs | null] | null = null;
  if (defaultValue?.length) {
    _defaultVlue = [defaultValue[0] ? dayjs(defaultValue[0]) : null, defaultValue[1] ? dayjs(defaultValue[1]) : null];
  }

  return (
    <Form.Item {...itemProps} labelCol={labelCol} wrapperCol={wrapperCol}>
      <JalaliDatePicker.RangePicker defaultValue={_defaultVlue} {...rest} />
    </Form.Item>
  );
};

DefaultForm.RangePicker = RangePicker;

export { DefaultForm };
